import { DeviceRulesResponse, PauseInternetRule } from '../types/api/types';
import { getDeviceAndUserStore, getRulesStore } from '../storage/stores';
import { getApi } from '.';
import { nowImmediate } from '../lib/dateTime';
import { parsePauseInternetEndsAtInSeconds } from '../helper/pauseInternet';
import { findNextScreenFreeTimeStartMs, mapRoutineRules } from './routineRules';
import {
  PauseInternetRuleStateStore,
  RulesStore,
  TimeRestrictionsStore,
} from '../types/store';

/**
 * Weekly routines can have their next start on another day, but the alarm
 * still needs enough lead time to wake the service worker 15 minutes before.
 */
const SCREEN_FREE_TIME_NEXT_START_LOOK_AHEAD_MS = 7 * 24 * 60 * 60 * 1000;

const normalizePauseInternet = (
  rule: PauseInternetRule | undefined
): PauseInternetRuleStateStore | undefined => {
  if (!rule) {
    return undefined;
  }
  return { endsAtInSeconds: parsePauseInternetEndsAtInSeconds(rule.ends_at) };
};

export const fetchRules = async ({
  forceRefresh = false,
}: {
  forceRefresh?: boolean;
} = {}): Promise<RulesStore> => {
  const deviceAndUser = getDeviceAndUserStore().get();
  if (!deviceAndUser) {
    throw new Error('Device and user store not initialized');
  }

  const currentTimestamp = forceRefresh
    ? undefined
    : getRulesStore().get()?.timestamp;
  const headers = currentTimestamp
    ? { 'DEVICE-POLICY-TIMESTAMP': String(currentTimestamp) }
    : undefined;

  const { data }: DeviceRulesResponse = await getApi().deviceRules.get(
    {
      deviceUId: deviceAndUser.device,
    },
    undefined, // query params
    headers
  );

  const ruleId = data.users[deviceAndUser.user]?.rules;
  if (!ruleId || !data.rules[ruleId]) {
    throw new Error('Invalid rules data');
  }

  const { timestamp, timezone } = data;
  const {
    time_restrictions: timeRestrictions,
    daily_quota: dailyQuota,
    extra_time: extraTime,
    routines,
    web,
    safe_networks_rules: safeNetworks,
  } = data.rules[ruleId];
  const {
    categories,
    is_allow_unknown_sites: allowUnknownSites,
    is_category_list: isCategoryList,
    is_safe_search: isSafeSearch,
    domains: domainRules,
  } = web;
  const safeNetworksRules = {
    delegateProtectionInSafeNetwork:
      safeNetworks.disable_protection_in_safe_network,
  };
  const { pause_internet, ...restTimeRestrictions } = timeRestrictions;
  const normalizedTimeRestrictions: TimeRestrictionsStore = {
    ...restTimeRestrictions,
    pause_internet: normalizePauseInternet(pause_internet),
  };
  const rulesStore: RulesStore = {
    timestamp,
    timezone,
    timeRestrictions: normalizedTimeRestrictions,
    categories,
    allowUnknownSites,
    isCategoryList,
    isSafeSearch,
    domainRules,
    safeNetworksRules,
  };

  if (extraTime) {
    rulesStore.dailyQuota = {
      extraTimeMinutes: extraTime.minutes ?? 0,
      extraTimeEndsAtInSeconds: extraTime.ends_at ?? 0,
    };
  } else if (dailyQuota) {
    rulesStore.dailyQuota = {
      extraTimeMinutes: dailyQuota.extra_time_minutes,
    };
  }

  if (routines) {
    const nowMs = nowImmediate();
    rulesStore.routines = mapRoutineRules(routines, nowMs, timezone);
    const nextStartMs = findNextScreenFreeTimeStartMs(
      routines,
      nowMs,
      timezone,
      SCREEN_FREE_TIME_NEXT_START_LOOK_AHEAD_MS
    );
    if (nextStartMs !== undefined) {
      rulesStore.nextScreenFreeTimeRoutineStartsAtInSeconds = Math.floor(
        nextStartMs / 1000
      );
    }
  }

  return rulesStore;
};
